import { useCallback, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import BatchSubscriptions from "./features/BatchSubscriptions";
import Login from "./features/Login/Login";
import ErrorPage from "./features/Navigation/errorPage";
import { tokenValidate } from "./core/services/TokenService";
import { ThemeProvider } from "@mui/material";
import { LoadingContext } from "./core/utils/loading";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers";
import NotFoundPage from "./features/Navigation/notFoundPage";
import theme from "./core/theme";
import { Rater, Quote } from "./features/Rater";
import "moment/locale/fr";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GraphicProvider, useGraphic } from "./core/utils/graphic-context";
import { Helmet } from "react-helmet";

const CHECKING = 1,
  CONNECTED = 2,
  DISCONNECTED = 3;

// Build route list dynamically based on brand
// BRAND RULES:
// - REACT_APP_BRAND=ffve => only /rate/:productCode is accessible. All other paths redirect to external collection site.
// - REACT_APP_BRAND=carene (or anything else) => normal behavior (no restriction).
const BRAND = process.env.REACT_APP_BRAND;
const REDIRECT_URL = process.env.REACT_APP_REDIRECT_URI;

const baseRoutes = [
  /* Authentification */
  { path: "/login", guarded: false, element: <Login /> },
  { path: "/", guarded: DISCONNECTED, element: <Login /> },
  { path: "/rate/:productCode", guarded: false, element: <Rater /> },
  { path: "/quote/:quoteId", guarded: false, element: <Quote /> },
  {
    path: "/failed-login",
    guarded: DISCONNECTED,
    element: <ErrorPage errorMsg="Tentative de connexion échouée !" />,
  },
  /* Souscription PROTECT en Masse */
  {
    path: "/",
    guarded: CONNECTED,
    element: <Navigate to="/batch-subscriptions/batches/list" />,
  },
  {
    path: "/batch-subscriptions/*",
    guarded: CONNECTED,
    element: <BatchSubscriptions />,
  },
  /* 404 */
  { path: "*", guarded: false, element: <NotFoundPage /> },
];

let routes = baseRoutes;
if (BRAND === "ffve") {
  // Restrict to only /rate/:productCode and any other path triggers external redirect.
  routes = [
    { path: "/rate/:productCode", guarded: false, element: <Rater /> },
    // catch-all redirect performed via element
    {
      path: "*",
      guarded: false,
      element: <ExternalRedirect url={REDIRECT_URL} />,
    },
  ];
}

function App() {
  const locale = "fr"; // Consider making this configurable
  const [loading, setLoading] = useState(0);
  const [authCheck, setAuthCheck] = useState(CHECKING);
  const startLoading = useCallback(() => setLoading((v) => v + 1), []);
  const endLoading = useCallback(() => setLoading((v) => v - 1), []);

  useEffect(() => {
    const TASK_KEYS = ["quote-create"];
    const handleBeforeUnload = (event) => {
      const hasPendingTask = TASK_KEYS.some((key) => {
        const item = sessionStorage.getItem(key);
        if (item) {
          try {
            const { status } = JSON.parse(item);
            return status === "pending";
          } catch (error) {
            console.error(`Error parsing sessionStorage key ${key}:`, error);
            return false;
          }
        }
        return false;
      });

      if (hasPendingTask) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    startLoading();
    tokenValidate()
      .then((isValid) => {
        setAuthCheck(isValid ? CONNECTED : DISCONNECTED);
        endLoading();
      })
      .catch((error) => {
        console.error("Error during token validation:", error); // Log the error
        setAuthCheck(DISCONNECTED);
        endLoading();
      });
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 0,
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <LoadingContext.Provider value={{ startLoading, endLoading }}>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale={locale}
          >
            <GraphicProvider>
              <HeaderContent loading={loading} />
              <Router>
                <Routes>
                  {routes
                    .filter(
                      (route) => !route.guarded || route.guarded === authCheck
                    )
                    .map((route, i) => (
                      <Route
                        key={i}
                        path={route.path}
                        element={route.element}
                      />
                    ))}
                </Routes>
              </Router>
            </GraphicProvider>
          </LocalizationProvider>
        </QueryClientProvider>
      </LoadingContext.Provider>
    </ThemeProvider>
  );
}

// Dedicated component for external redirection to avoid React Router interpreting it as internal path.
function ExternalRedirect({ url }) {
  useEffect(() => {
    window.location.replace(url); // replace history so back won't loop
  }, [url]);
  return null;
}

function HeaderContent({ loading }) {
  const { title, icon, config } = useGraphic();

  return (
    <>
      <Helmet>
        {title && <title>{title}</title>}
        {icon && <link rel="icon" type="image/png" href={icon} />}
        {config && config.css && (
          <link rel="stylesheet" type="text/css" href={config.css} />
        )}
      </Helmet>
      {loading > 0 && (
        <div className="progress-bar">
          <div className="progress-bar-value" />
        </div>
      )}
    </>
  );
}

export default App;
